/**
 * inflightAnalyzeFit.js — RC 1.49.x Slice 2B (2026-05-07).
 *
 * Persistent in-flight marker for the Analyse Fit action. Mirrors
 * the pattern already used by `submitIngestUrlWithRecovery` for the
 * job-import 504 path, only here it lives in `localStorage` (not
 * `sessionStorage`) so it survives a full window close + relaunch.
 *
 * Why a marker?
 * ─────────────
 * The user clicks Analyse Fit, switches browser tabs, comes back
 * later (sometimes much later — minutes), and has historically had
 * three possible outcomes:
 *
 *   1. The polling helper finished while they were away → toast
 *      fired → `setJob(prev => ({ ...prev, user_score: ... }))`
 *      committed → all good.
 *   2. The page unmounted while polling (route change, browser
 *      backgrounded the SPA, etc.) → AbortController fired →
 *      `AbortError` swallowed → backend still wrote `job_scores`
 *      but the page never learned.
 *   3. The polling helper is still running on return → button
 *      keeps spinning → eventual toast fires.
 *
 * Outcome (2) is the one Antonia hit. The marker lets a freshly-
 * mounted JobDetailPage (or a still-mounted one whose tab just
 * returned) check "did I have an analysis pending for THIS job?"
 * and, if yes, do exactly one targeted re-fetch of /api/jobs/{id}
 * to collect the score the backend already wrote.
 *
 * Hard rails:
 *   • No backend changes / no new endpoint / no async architecture.
 *   • localStorage only — never sessionStorage (must survive new
 *     tab opens), never IndexedDB (overkill for a 5-minute marker).
 *   • Per-(user, job) keying so simultaneous Analyse Fit on a
 *     different job doesn't get confused by a sibling marker.
 *   • Strict TTL (default 5 minutes). After TTL the marker is
 *     considered abandoned and the UI surfaces a calm retry hint.
 */

/** Default TTL for an in-flight marker. Backend Analyse Fit
 *  typically completes in < 60 s; the polling helper's max wait
 *  is ~3 minutes (see `analyseFitPolling.MAX_POLLS * POLL_DELAYS_MS`).
 *  5 minutes gives a clean buffer for the slowest LLM run + a
 *  small grace window before the UI gives up. */
export const INFLIGHT_TTL_MS = 5 * 60 * 1000;

/** Build the localStorage key for a given (user, job) pair. */
export function inflightKey(userId, jobId) {
  return `inflight-analyze-fit:${userId || 'anon'}:${jobId}`;
}

/** Write a fresh marker. Idempotent — overwrites any previous
 *  marker for the same (user, job). */
export function writeInflightAnalyzeFit(userId, jobId) {
  if (typeof window === 'undefined') return;
  if (!jobId) return;
  try {
    window.localStorage.setItem(
      inflightKey(userId, jobId),
      JSON.stringify({ startedAtMs: Date.now(), jobId }),
    );
  } catch { /* never throw on storage failure */ }
}

/** Read the marker, or `null` if absent / expired / malformed. */
export function readInflightAnalyzeFit(userId, jobId, ttlMs = INFLIGHT_TTL_MS) {
  if (typeof window === 'undefined') return null;
  if (!jobId) return null;
  try {
    const raw = window.localStorage.getItem(inflightKey(userId, jobId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const { startedAtMs, jobId: markerJobId } = parsed;
    if (markerJobId !== jobId) return null;
    if (typeof startedAtMs !== 'number') return null;
    return { startedAtMs, jobId: markerJobId };
  } catch {
    return null;
  }
}

/** Clear the marker for (user, job). Idempotent. */
export function clearInflightAnalyzeFit(userId, jobId) {
  if (typeof window === 'undefined') return;
  if (!jobId) return;
  try {
    window.localStorage.removeItem(inflightKey(userId, jobId));
  } catch { /* never throw on storage failure */ }
}

/** Convenience predicate: marker exists and is still within TTL. */
export function hasFreshInflightAnalyzeFit(userId, jobId, ttlMs = INFLIGHT_TTL_MS) {
  const m = readInflightAnalyzeFit(userId, jobId, ttlMs);
  if (!m) return false;
  return (Date.now() - m.startedAtMs) < ttlMs;
}

/** Convenience predicate: marker exists but TTL has elapsed. */
export function hasExpiredInflightAnalyzeFit(userId, jobId, ttlMs = INFLIGHT_TTL_MS) {
  const m = readInflightAnalyzeFit(userId, jobId, ttlMs);
  if (!m) return false;
  return (Date.now() - m.startedAtMs) >= ttlMs;
}

/** Humanise a thrown Analyse Fit error into actionable user copy.
 *  Targeted backend `detail.code` strings still pass through
 *  verbatim where the existing modal/UX wires expect them.
 *  Returns null when the caller should rely on its own error
 *  branch (e.g. `USAGE_EXHAUSTED` opens the upsell modal). */
export function humaniseAnalyzeFitError(err) {
  const status = err?.status;
  const code = err?.data?.detail?.code;
  const raw = (err?.message || '').trim();

  // Operator-locked: status codes the existing handler already
  // consumes via dedicated UX branches (modals, redirects). Tell
  // the caller "I won't speak for you" by returning null.
  if (code === 'USAGE_EXHAUSTED') return null;
  if (code === 'FREE_DAILY_ANALYZE_LIMIT_REACHED') return null;

  // Targeted backend signals the polling helper / extractor surface.
  // These run BEFORE the generic 4xx-passthrough below so a 400
  // / 422 with a humanisable phrase always wins over the raw text.
  if (/missing\s+(cv|source\s*cv|primary\s*cv)/i.test(raw)) {
    return "Please add a Source CV before running Analyse Fit. You can upload one from your Documents page.";
  }
  if (/insufficient\s+(job\s*description|description)|description\s+too\s+short/i.test(raw)) {
    return "This job's description is too short to analyse reliably. Please paste the full description and try again.";
  }
  if (/extraction\s+(failed|incomplete)/i.test(raw)) {
    return "We couldn't extract enough detail from this job posting. Please paste the full description and try again.";
  }
  if (status === 408 || /timeout|timed?\s*out/i.test(raw)) {
    return "Analyse Fit took longer than expected. The result may still arrive shortly — please wait a moment, or try again.";
  }

  // 4xx with a real, user-shaped detail string: pass through.
  if (status >= 400 && status < 500 && raw && !/^request failed/i.test(raw)) {
    return raw;
  }

  // Generic 5xx / "internal server error" / empty messages.
  const looksGeneric = !raw
    || /^internal server error\.?$/i.test(raw)
    || /^server error\.?$/i.test(raw)
    || /^request failed( with status code \d+)?\.?$/i.test(raw);
  if (looksGeneric || (status >= 500 && status < 600)) {
    return "Something went wrong on our side. Please try again in a moment — your credits were not charged for failed analyses.";
  }

  return raw || 'Analyse Fit failed.';
}
